import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Divider,
	Grid2,
	IconButton,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import type { StraightenerParameterRow } from "../models/StraightenerParamLimit";

interface Props {
	row: StraightenerParameterRow | null;
	isSaving: boolean;
	onSave: (
		code: string,
		min: number | null,
		max: number | null,
		sendToPlc: boolean | null,
		isRequired: boolean | null,
	) => void;
	onClose: () => void;
	// Reports unsaved edits so the manager can guard row navigation.
	onDirtyChange?: (dirty: boolean) => void;
}

const numOrNull = (s: string): number | null =>
	s.trim() === "" ? null : Number(s);

export const StraightenerLimitDetailViewer = ({
	row,
	isSaving,
	onSave,
	onClose,
	onDirtyChange,
}: Props) => {
	const [min, setMin] = useState("");
	const [max, setMax] = useState("");
	const [sendToPlc, setSendToPlc] = useState(false);
	const [required, setRequired] = useState(false);

	useEffect(() => {
		setMin(row?.min != null ? String(row.min) : "");
		setMax(row?.max != null ? String(row.max) : "");
		setSendToPlc(row?.sendToPlc ?? false);
		setRequired(row?.isRequired ?? false);
	}, [row?.code, row?.min, row?.max, row?.sendToPlc, row?.isRequired]);

	const dirty =
		row != null &&
		(min !== (row.min != null ? String(row.min) : "") ||
			max !== (row.max != null ? String(row.max) : "") ||
			sendToPlc !== row.sendToPlc ||
			required !== row.isRequired);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);

	if (!row) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a parameter to edit its configuration</Typography>
			</Box>
		);
	}

	// Read-back params are display only: never sent, never required.
	const canToggleFlags = !row.readOnly;
	// Business rule: sent to the PLC => required (the backend enforces it too).
	const requiredLocked = sendToPlc;
	// Numeric limits only make sense for editable numeric parameters.
	const canEditLimits = row.valueType === "numeric" && !row.readOnly;

	const minN = numOrNull(min);
	const maxN = numOrNull(max);
	const invalid = minN != null && maxN != null && minN >= maxN;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 2,
					py: 1.25,
				}}
			>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					{row.displayName}
				</Typography>
				<Tooltip title="Close">
					<IconButton size="small" onClick={onClose} disabled={isSaving}>
						<CloseIcon />
					</IconButton>
				</Tooltip>
			</Box>
			<Divider />

			<Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
				<Typography
					sx={{
						color: "primary.main",
						fontWeight: 600,
						fontSize: "0.85rem",
						mb: 1,
					}}
				>
					Validation limits
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Section: <strong>{row.group}</strong>
				</Typography>

				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 6 }}>
						<TextField
							label="Min"
							type="number"
							value={min}
							onChange={(e) => setMin(e.target.value)}
							disabled={!canEditLimits}
							fullWidth
							size="small"
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 6 }}>
						<TextField
							label="Max"
							type="number"
							value={max}
							onChange={(e) => setMax(e.target.value)}
							error={invalid}
							helperText={invalid ? "Max must be greater than Min" : undefined}
							disabled={!canEditLimits}
							fullWidth
							size="small"
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
				</Grid2>

				<Grid2 container spacing={2} sx={{ mt: 3 }}>
					<Grid2 size={{ xs: 6 }}>
						<Typography
							sx={{
								color: "primary.main",
								fontWeight: 600,
								fontSize: "0.85rem",
								mb: 0.5,
							}}
						>
							Send to PLC
						</Typography>
						<Switch
							checked={sendToPlc}
							onChange={(e) => {
								setSendToPlc(e.target.checked);
								if (e.target.checked) setRequired(true);
							}}
							disabled={!canToggleFlags}
						/>
					</Grid2>
					<Grid2 size={{ xs: 6 }}>
						<Typography
							sx={{
								color: "primary.main",
								fontWeight: 600,
								fontSize: "0.85rem",
								mb: 0.5,
							}}
						>
							Required
						</Typography>
						<Switch
							checked={required}
							onChange={(e) => setRequired(e.target.checked)}
							disabled={!canToggleFlags || requiredLocked}
						/>
					</Grid2>
				</Grid2>
			</Box>

			<Divider />
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 1,
					px: 2,
					py: 1.5,
				}}
			>
				<Button
					variant="contained"
					color="primary"
					onClick={() =>
						onSave(
							row.code,
							minN,
							maxN,
							canToggleFlags ? sendToPlc : null,
							canToggleFlags ? required : null,
						)
					}
					disabled={isSaving || invalid || !dirty}
				>
					SAVE
				</Button>
			</Box>
		</Box>
	);
};
